<template>
  <v-dialog v-model="dialogVisible" max-width="700">
    <v-card class="edit-modal">
      <v-card-title class="edit-modal__header">
        <v-icon left>{{ isEditMode ? "mdi-pencil" : "mdi-plus" }}</v-icon>
        {{ isEditMode ? "Edit Building" : "Create New Building" }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="edit-modal__content">
        <v-form ref="form" v-model="formValid" lazy-validation>
          <!-- Name Field -->
          <v-text-field
            v-model="formData.name"
            label="Building Name"
            prepend-icon="mdi-office-building"
            :rules="nameRules"
            outlined
            required
          ></v-text-field>

          <!-- Description Field -->
          <v-textarea
            v-model="formData.description"
            label="Description"
            prepend-icon="mdi-text"
            outlined
            rows="3"
            auto-grow
            hint="Provide a brief description of the building"
          ></v-textarea>

          <!-- Address Field -->
          <v-text-field
            v-model="formData.address"
            label="Address"
            prepend-icon="mdi-map-marker"
            outlined
            hint="Street address of the building"
          ></v-text-field>

          <!-- GPS Coordinates -->
          <v-text-field
            v-model="formData.gps"
            label="GPS Coordinates"
            prepend-icon="mdi-crosshairs-gps"
            outlined
            hint="Format: latitude, longitude (e.g., 48.8566, 2.3522)"
            :rules="gpsRules"
          ></v-text-field>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="edit-modal__actions">
        <v-btn
          v-if="isEditMode"
          color="error"
          text
          @click="$emit('delete', building)"
        >
          <v-icon left>mdi-delete</v-icon>
          Delete
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!formValid"
          :loading="saving"
          @click="save"
        >
          <v-icon left>mdi-content-save</v-icon>
          {{ isEditMode ? "Save Changes" : "Create Building" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "BuildingEditModal",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    building: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close", "save", "delete"],

  data() {
    return {
      formValid: true,
      formData: {
        name: "",
        description: "",
        address: "",
        gps: "",
      },
      nameRules: [
        (v) => !!v || "Building name is required",
        (v) => (v && v.length >= 2) || "Name must be at least 2 characters",
      ],
      gpsRules: [
        (v) =>
          !v ||
          /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(v) ||
          "Invalid GPS format. Use: latitude, longitude",
      ],
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("close", val);
      },
    },

    isEditMode() {
      return this.building && this.building.id && !this.building.temp;
    },
  },

  methods: {
    close() {
      this.resetForm();
      this.dialogVisible = false;
    },

    resetForm() {
      this.formData = {
        name: "",
        description: "",
        address: "",
        gps: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    populateForm() {
      if (this.building) {
        this.formData = {
          name: this.building.name || "",
          description: this.building.description || "",
          address: this.building.address || "",
          gps: this.building.gps || "",
        };
      } else {
        this.resetForm();
      }
    },

    async save() {
      if (this.$refs.form.validate()) {
        this.$emit("save", {
          ...this.building,
          ...this.formData,
        });
      }
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.populateForm();
      }
    },

    building: {
      handler() {
        if (this.value) {
          this.populateForm();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.edit-modal__header {
  background-color: var(--primaryColor);
  color: var(--backgroundColor) !important;
  align-items: center;
}

.edit-modal__content {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
